import {useState, useCallback, useMemo} from 'react'
import {TabItem} from 'wix-ui-tpa/cssVars'
import {Experience as ExperienceProto} from '@wix/ambassador-table-reservations-v1-experience/types'
import {ReservationLocation as ReservationLocationProto} from '@wix/ambassador-table-reservations-v1-reservation-location/types'

import {useExperiencesListStorage} from '../storage'

const ALL_RESERVATION_LOCATIONS_OPTION = 0

export const useHooks = ({
  allReservationLocationsTabText,
}: {
  allReservationLocationsTabText: string
}) => {
  const {
    // layout
    breadcrumbsConfig,
    regionalSettings,
    reservationLocations: allReservationLocations,
    experiences: allExperiences,
    goToExperienceDetailsPage,
  } = useExperiencesListStorage()

  const [selectedReservationLocationTabIndex, setSelectedReservationLocationTabIndex] = useState(
    ALL_RESERVATION_LOCATIONS_OPTION,
  )

  const {reservationLocationsMap, filteredExperiences} = useMemo(
    () => filterReservationLocationsAndExperiences(allReservationLocations, allExperiences),
    [allReservationLocations, allExperiences],
  )

  const reservationLocationTabOptions: TabItem[] = useMemo(() => {
    const allReservationLocationsOption: TabItem = {
      id: 'all',
      title: allReservationLocationsTabText,
    }

    const restReservationLocationOptions = Object.values(reservationLocationsMap).map(
      ({id, location}) => ({
        id: id!,
        title: location?.name!,
      }),
    )

    return [allReservationLocationsOption, ...restReservationLocationOptions]
  }, [allReservationLocationsTabText, reservationLocationsMap])

  const selectedExperiences =
    selectedReservationLocationTabIndex === ALL_RESERVATION_LOCATIONS_OPTION
      ? filteredExperiences
      : filteredExperiences.filter(
          ({reservationLocationId}) =>
            reservationLocationId ===
            reservationLocationTabOptions[selectedReservationLocationTabIndex].id,
        )

  const handleReservationLocationSelect = useCallback((index: number) => {
    setSelectedReservationLocationTabIndex(index)
  }, [])

  const handleGoToExperience = useCallback(
    (experienceId: string) => {
      goToExperienceDetailsPage(experienceId)
    },
    [goToExperienceDetailsPage],
  )

  return {
    breadcrumbsConfig,
    regionalSettings,
    selectedReservationLocationTabIndex,
    reservationLocationTabOptions,
    reservationLocationsMap,
    selectedExperiences,
    handleReservationLocationSelect,
    handleGoToExperience,
  }
}

const filterReservationLocationsAndExperiences = (
  reservationLocations: ReservationLocationProto[],
  experiences: ExperienceProto[],
) => {
  const unarchivedReservationLocations = reservationLocations.filter(({archived}) => !archived)
  const visibleExperiences = experiences.filter(({configuration}) => configuration?.visible)

  const reservationLocationsMap: Record<string, ReservationLocationProto> = {}

  const experiencesWithExistingReservationLocation = visibleExperiences.filter(
    ({reservationLocationId}) => {
      const experienceReservationLocation = unarchivedReservationLocations.find(
        ({id}) => id === reservationLocationId,
      )

      if (experienceReservationLocation) {
        reservationLocationsMap[experienceReservationLocation.id!] = experienceReservationLocation
        return true
      }

      return false
    },
  )

  return {
    reservationLocationsMap,
    filteredExperiences: experiencesWithExistingReservationLocation,
  }
}
